'use client';

import { useCallback, useEffect, useState } from 'react';
import { Box, Container, Typography, Stack } from '@mui/material';

// ⛔️ Mock imports — you'll replace with MUI versions
import { ProfileHeader } from './components/ProfileHeader';
import { StatsCards } from './components/StatsCard';
import { JobList } from './components/JobList';
import { Pagination } from './components/Pagination';
import { SegmentedControl } from './components/SegmentedControl';
import { RequestsList } from './components/RequestsList';
import { InvitationsList } from './components/InvitationsList';
import { EmptyState } from './components/EmptyState';
import { SocialUsername } from './components/SocialUsername';
import { EditProfileModal } from './components/EditProfileModal';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { useDispatch } from 'react-redux';
import { navigate } from '../../redux/navigator/action';
import { replaceRouteParam } from '../../utils/routesProcessor';
import { ROUTES } from '../../types/global/routes.types';
import { useDispatchPromise } from '../../utils/AppUtils';
import { getBuzzBridgeProfile, updateBuzzBridgeProfile } from '../../redux/buzzbridge/actions';
import { useSelector } from 'react-redux';
import { buzzProfileWithLoadingSelector } from '../../selectors/selectors';
import { IInfluencer } from '../../redux/buzzbridge/types';

export const BuzzBridgeJobDashboard = () => {
    const { themeStyle } = useThemeContext();
    const [, setCurrentPage] = useState(1);
    const [activeTab, setActiveTab] = useState('jobs');
    const [tabLoading, setTabLoading] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const { data, gettingBuzzProfile } = useSelector(buzzProfileWithLoadingSelector);
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();

    // Load initial profile data (header and stats)
    const handleGetInitialProfile = useCallback(async () => {
        await dispatchPromise(getBuzzBridgeProfile({}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load tab-specific data
    const handleGetTabData = useCallback(async (tab: string, page: number = 1) => {
        setTabLoading(true);
        const groupType = tab === 'jobs' ? 'active_jobs' : tab === 'requests' ? 'requests' : 'invitations';
        await dispatchPromise(
            getBuzzBridgeProfile({ group_type: groupType as any, page, limit: 10, skipRefresh: true }),
        );
        setTabLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        handleGetInitialProfile();
    }, [handleGetInitialProfile]);

    const handleTabChange = (tab: string) => {
        setActiveTab(tab);
        setCurrentPage(1);
        handleGetTabData(tab, 1);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        handleGetTabData(activeTab, page);
    };

    const handleSeeDetails = (jobId: string) => {
        const route = replaceRouteParam(ROUTES.BUZZ_BRIDGE_DETAILS, 'campaignId', jobId);
        dispatch(
            navigate({
                routes: `${route}`,
                skipAuthentication: false,
            }),
        );
    };

    const handleAcceptInvitation = (invitationId: string) => {
        // TODO: Implement accept invitation logic
        console.log('Accept invitation:', invitationId);
    };

    const handleRejectInvitation = (invitationId: string) => {
        // TODO: Implement reject invitation logic
        console.log('Reject invitation:', invitationId);
    };

    const handleEditProfile = () => {
        setEditModalOpen(true);
    };

    const handleSaveProfile = (updatedData: Partial<IInfluencer>) => {
        if (data?.data.influencer._id)
            dispatch(
                updateBuzzBridgeProfile({
                    documentId: data?.data.influencer._id,
                    payload: updatedData,
                }),
            );
        setEditModalOpen(false);
    };

    const tabOptions = [
        { label: 'Your Jobs', value: 'jobs' },
        { label: 'Requests', value: 'requests' },
        { label: 'Invitations', value: 'invitations' },
    ];

    // Get current data based on active tab
    const getCurrentData = () => {
        if (!data?.data?.influencer) return [];

        switch (activeTab) {
            case 'jobs':
                return data.data.influencer.active_jobs || [];
            case 'requests':
                return data.data.influencer.requests || [];
            case 'invitations':
                return data.data.influencer.invitations || [];
            default:
                return [];
        }
    };

    // Get pagination info based on active tab
    const getPaginationInfo = () => {
        if (!data) return { currentPage: 1, totalPages: 1, hasNext: false, hasPrev: false };

        switch (activeTab) {
            case 'jobs':
                return {
                    currentPage: data.currentPage,
                    totalPages: data.totalPages?.active_jobs || 1,
                    hasNext: data.hasNextPage?.active_jobs || false,
                    hasPrev: data.hasPreviousPage?.active_jobs || false,
                };
            case 'requests':
                return {
                    currentPage: data.currentPage,
                    totalPages: data.totalPages?.requests || 1,
                    hasNext: data.hasNextPage?.requests || false,
                    hasPrev: data.hasPreviousPage?.requests || false,
                };
            case 'invitations':
                return {
                    currentPage: data.currentPage,
                    totalPages: data.totalPages?.invitations || 1,
                    hasNext: data.hasNextPage?.invitations || false,
                    hasPrev: data.hasPreviousPage?.invitations || false,
                };
            default:
                return { currentPage: 1, totalPages: 1, hasNext: false, hasPrev: false };
        }
    };

    const currentData = getCurrentData();
    const paginationInfo = getPaginationInfo();

    // Calculate stats from API data
    const calculateStats = () => {
        if (!data?.data?.influencer) {
            return { activeJobs: 0, completedJobs: 0, averageRating: 0 };
        }

        const influencer = data.data.influencer;
        return {
            activeJobs: influencer.active_jobs_count || 0,
            completedJobs: influencer.completed_campaigns || 0,
            averageRating: influencer.ratings || 0,
        };
    };

    const statsData = calculateStats();

    // Render social usernames
    const renderSocialUsernames = () => {
        if (!data?.data?.influencer) return null;

        const influencer = data.data.influencer;
        const usernames = [];

        if (influencer.instagram?.username) {
            usernames.push(
                <SocialUsername
                    key="instagram"
                    platform="Instagram"
                    username={influencer.instagram.username}
                    size="small"
                />,
            );
        }

        if (influencer.youtube?.username) {
            usernames.push(
                <SocialUsername key="youtube" platform="YouTube" username={influencer.youtube.username} size="small" />,
            );
        }

        if (influencer.tiktok?.username) {
            usernames.push(
                <SocialUsername key="tiktok" platform="TikTok" username={influencer.tiktok.username} size="small" />,
            );
        }

        return usernames.length > 0 ? (
            <Stack direction="row" spacing={2} flexWrap="wrap" gap={1}>
                {usernames}
            </Stack>
        ) : null;
    };

    // Render tab content with loading state
    const renderTabContent = () => {
        if (tabLoading) {
            return (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography variant="body1" sx={{ color: theme[themeStyle].tag_white }}>
                        Loading {activeTab}...
                    </Typography>
                </Box>
            );
        }

        if (currentData.length === 0) {
            const emptyStateConfig = {
                jobs: {
                    title: 'No Active Jobs',
                    message: "You don't have any active jobs at the moment. Check back later for new opportunities!",
                },
                requests: {
                    title: 'No Requests',
                    message:
                        "You haven't made any requests yet. Start applying to campaigns to see your requests here!",
                },
                invitations: {
                    title: 'No Invitations',
                    message:
                        "You don't have any invitations at the moment. Brands will send you invitations when they're interested in working with you!",
                },
            };

            const config = emptyStateConfig[activeTab as keyof typeof emptyStateConfig];
            return <EmptyState title={config.title} message={config.message} />;
        }

        return (
            <>
                {activeTab === 'jobs' && <JobList jobs={currentData} onSeeDetails={handleSeeDetails} />}
                {activeTab === 'requests' && <RequestsList requests={currentData} />}
                {activeTab === 'invitations' && (
                    <InvitationsList
                        invitations={currentData}
                        onAccept={handleAcceptInvitation}
                        onReject={handleRejectInvitation}
                    />
                )}
                <Pagination
                    currentPage={paginationInfo.currentPage}
                    totalPages={paginationInfo.totalPages}
                    onPageChange={handlePageChange}
                />
            </>
        );
    };

    if (gettingBuzzProfile) {
        return (
            <Box sx={{ minHeight: '100vh', py: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="h6" sx={{ color: theme[themeStyle].tag_white }}>
                    Loading...
                </Typography>
            </Box>
        );
    }

    if (!gettingBuzzProfile && !data) {
        return (
            <Box sx={{ minHeight: '100vh', py: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <EmptyState
                    message="No BuzzBidge profile found for this account. Please apply to be included"
                    title="No profile found"
                />
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', py: 4 }}>
            <Container disableGutters maxWidth="lg">
                <Stack spacing={5}>
                    {data?.data?.influencer && (
                        <>
                            <ProfileHeader influencer={data.data.influencer} onEdit={handleEditProfile} />
                            <StatsCards stats={statsData} />
                        </>
                    )}

                    <Stack spacing={3}>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems={{
                                xs: 'flex-start',
                                sm: 'center',
                            }}
                            flexDirection={{ xs: 'column', sm: 'row' }}
                            gap={1}>
                            <Typography variant="h5" fontWeight={600} sx={{ color: theme[themeStyle].tag_white }}>
                                Work Management
                            </Typography>
                            <SegmentedControl options={tabOptions} value={activeTab} onChange={handleTabChange} />
                        </Box>

                        {/* Social Usernames */}
                        {renderSocialUsernames()}

                        {/* Tab Content */}
                        {renderTabContent()}
                    </Stack>
                </Stack>
            </Container>

            {/* Edit Profile Modal */}
            {data?.data?.influencer && (
                <EditProfileModal
                    open={editModalOpen}
                    onClose={() => setEditModalOpen(false)}
                    influencer={data.data.influencer}
                    onSave={handleSaveProfile}
                />
            )}
        </Box>
    );
};
